import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { LockOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const { login, verifyOtp, otpRequired, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showOtpForm, setShowOtpForm] = useState(false);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    
    try {
      const result = await login(email, password);
      
      if (result?.requiresOtp) {
        setShowOtpForm(true);
      } else {
        // Redirect to the page user tried to visit or dashboard
        navigate(from, { replace: true });
      }
    } catch (err) {
      console.error("Login error:", err);
      if (err.response && err.response.data) {
        // Handle specific API error messages
        if (err.response.data.detail) {
          setLoginError(err.response.data.detail);
        } else if (typeof err.response.data === 'object') {
          // Handle validation errors
          const firstErrorKey = Object.keys(err.response.data)[0];
          const errorMessage = err.response.data[firstErrorKey];
          setLoginError(`${firstErrorKey}: ${errorMessage}`);
        } else {
          setLoginError('Login failed. Please check your credentials.');
        }
      } else {
        setLoginError(err.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    
    try {
      await verifyOtp(otp);
      // Redirect to the page user tried to visit or dashboard
      navigate(from, { replace: true });
    } catch (err) {
      console.error("OTP verification error:", err);
      if (err.response && err.response.data) {
        // Handle specific API error messages
        if (err.response.data.detail) {
          setLoginError(err.response.data.detail);
        } else if (typeof err.response.data === 'object') {
          // Handle validation errors (e.g. if otp is incorrect)
          const firstErrorKey = Object.keys(err.response.data)[0];
          const errorMessage = err.response.data[firstErrorKey];
          setLoginError(`${firstErrorKey}: ${errorMessage}`);
        } else {
          setLoginError('OTP verification failed. Please try again.');
        }
      } else {
        setLoginError(err.message || 'OTP verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlined />
          </Avatar>

          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            {showOtpForm ? 'Enter OTP' : 'Login'}
          </Typography>

          {loginError && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {loginError}
            </Alert>
          )}

          {!showOtpForm ? (
            <Box component="form" onSubmit={handleLogin} sx={{ width: '100%', mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleOtpSubmit} sx={{ width: '100%', mt: 1 }}>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                A verification code has been sent to your email.
              </Typography>
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="otp"
                label="Verification Code"
                name="otp"
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                disabled={loading}
                inputProps={{ maxLength: 6 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify'}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
